


import React, { Fragment, useEffect, useState } from "react";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { withStyles, makeStyles } from "@material-ui/core/styles";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InputBase from "@material-ui/core/InputBase";
import Popper from "@material-ui/core/Popper";
import { titleCase } from "../titleCaseFunction";
import { useCustomMediaQuery } from "../hooks/useCustomMediaQuery";
import Checkbox from "@material-ui/core/Checkbox";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";

// utility: style mapping
const styledBy = (property, mapping) => (props) => mapping[props[property]];

/* ----------------- STYLES ----------------- */
const styles = {
  root: {
    backgroundColor: "white",
    "& .MuiInputBase-root": {
      fontSize: "12px",          // Global font size
      borderRadius: "8px",       // Softer corners
      padding: "8px",            // Compact padding
      height: "36px",            // Slightly taller for readability
      border: styledBy("color", {
        default: "1px solid #ced4da",  // Default border
        error: "1px solid red",        // Error state border
      }),
      width: "100%",              // Ensure it stretches fully
    },
    "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
      padding: "4px",            // Compact padding inside autocomplete
      fontSize: "12px",
    },
  },

  root_multiple: {
    backgroundColor: "white",
    "& .MuiInputBase-root": {
      fontSize: "12px",
      borderRadius: "8px",
      padding: "8px",
      border: styledBy("color", {
        default: "1px solid #ced4da",
        error: "1px solid red",
      }),
      width: "100%",
    },
    "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
      padding: "4px",
      fontSize: "12px",
    },
  },

  table: {
    "& .MuiTable-root": {
      width: "100%",
      borderCollapse: "collapse",
    },
    "& .MuiTableCell-root": {
      fontSize: "12px",
      padding: "6px 8px",
      border: "1px solid #e0e0e0",
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      backgroundColor: "#f8f9fa",
      fontWeight: "600",
      fontSize: "12px",
    },
  },
};


const AutoCompleteTextField = withStyles(styles)(
  ({ classes, color, is_multiple = false, ...other }) => (
    <TextField
      className={is_multiple ? classes.root_multiple : classes.root}
      {...other}
    />
  )
);

const input_styles = {
  input: {
    backgroundColor: "#f9fafb", // softer off-white for better readability
    border: styledBy("color", {
      default: "1px solid #ced4da",
      error: "1px solid #d9534f",
      placeholder: "1px solid #ced4da",
    }),
    color: styledBy("color", {
      default: "#333333", // dark gray for readability
      placeholder: "#888888", // softer gray for placeholders
    }),
    fontSize: "14px", // increased font size
    borderRadius: "8px",
    padding: "12px 14px", // more padding for better touch area
    height: "40px", // slightly taller for readability
    width: "100%",
    transition: "all 0.3s ease",
    "&:focus": {
      borderColor: "#007bff", // blue focus border
      boxShadow: "0 0 0 0.2rem rgba(0,123,255,.25)",
      backgroundColor: "#ffffff", // lighter on focus
    },
  },
};

const SelectInput = withStyles(input_styles)(({ classes, color, ...other }) => (
  <InputBase className={classes.input} {...other} />
));

const StyledMenuItem = withStyles((theme) => ({
  root: {
    fontSize: "14px", // increase font size for better readability
    color: "#333333",
    "&:focus": {
      backgroundColor: "#e6ebf5",
      "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
        color: "#333333",
      },
    },
  },
}))(MenuItem);

const useStyles = makeStyles((theme) => ({
  paper: {
    color: "#333333", // dark gray text
    fontSize: "14px", // increased font size
    background: "#fdfdfd", // soft white background
    boxShadow: "0 4px 14px rgba(27,31,35,.15)", // slightly deeper shadow
    borderRadius: "8px",
  },
  option: {
    minHeight: "40px",
    padding: "10px 12px",
    fontSize: "14px",
    '&[data-focus="true"]': {
      backgroundColor: "#e6ebf5", // soft highlight color
    },
  },
  selectMenu: {
    marginTop: "6px",
    maxHeight: "240px", // taller dropdown
    overflowY: "auto",
    scrollbarWidth: "thin",
    scrollbarColor: "#ccc transparent",
  },
}));



/* ----------------- HELPERS ----------------- */
const inValidateValue = (med_value, autocomplete_value, value_text) => {
  const isValueMed = !med_value
    ? true
    : typeof med_value === "string"
    ? false
    : value_text
    ? !med_value?.[value_text]
    : !med_value?.value;

  const isAutoCompleteValue = !autocomplete_value
    ? true
    : typeof med_value === "string"
    ? false
    : value_text
    ? !autocomplete_value?.[value_text]
    : !autocomplete_value?.value;

  return isValueMed && isAutoCompleteValue;
};

const PopperComponent = function (props) {
  return (
    <Popper
      {...props}
      style={{
        maxWidth: "max-content",
        border: "1px solid rgba(27,31,35,.15)",
        boxShadow: "0 3px 12px rgba(27,31,35,.15)",
        borderRadius: 10,
        zIndex: 1500,
        fontSize: 12,
        background: "white",
        marginTop: 5,
        marginBottom: 5,
      }}
      placement="bottom-start"
    />
  );
};

const icon = <RadioButtonUncheckedIcon style={{ fontSize: "15px" }} />;
const checkedIcon = <CheckCircleIcon style={{ fill: "green", fontSize: "15px" }} />;

/* ----------------- COMPONENTS ----------------- */

// Multiple select autocomplete
export const AutoCompleteMultiple = ({
  options,
  placeholder = "",
  selectedOptions = [],
  handleChange,
  name,
  submitted,
}) => {
  const classes = useStyles();
  return (
    <Autocomplete
      multiple
      options={options}
      onChange={handleChange}
      disableCloseOnSelect
      getOptionSelected={(option, value) => value?.label === option?.label}
      getOptionLabel={(option) => option.label || ""}
      renderOption={(option, { selected }) => (
        <Fragment>
          <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
          {option.label}
        </Fragment>
      )}
      value={selectedOptions}
      classes={{ option: classes.option, paper: classes.paper }}
      renderInput={(params) => (
        <AutoCompleteTextField
          {...params}
          variant="outlined"
          name={name}
          color={submitted && selectedOptions.length === 0 ? "error" : "default"}
          placeholder={placeholder}
          is_multiple
        />
      )}
    />
  );
};

// Uncontrolled autocomplete
export const AutoCompleteUnControlled = ({
  options,
  idx,
  handleChange,
  handleProductData,
  submitted,
  placeholder = "",
  name = "",
  medicine_value = {},
  label_text = "",
  value_text = "",
  id = "",
  testId = "",
  errorMsg = "",
  reset=false
}) => {
  const [value, setValue] = useState(medicine_value);
  const [inputValue, setInputValue] = useState("");
  const classes = useStyles();

  // console.log("medicine_value", medicine_value);
  // console.log("value", value);

  useEffect(() => {
    setValue(medicine_value);
  }, [medicine_value]);

  useEffect(() => {
    if(reset && reset==="retail"){
      setValue({value:"1", label:"Walk-in Customer"})
      setInputValue("Walk-in Customer")
    }else if(reset && reset==="wholesale"){
      setValue(null)
      setInputValue("")
    }else{
      return
    }
  }, [reset]);

  return (
    <div>
      <Autocomplete
        id={id}
        data-testid={testId}
        value={value || null}
        inputValue={inputValue}
        onChange={(event, newValue) => {
          setValue(newValue || "");
          if (!handleProductData) {
            handleChange(newValue);
          } else {
            const payload = {
              innerText:
                newValue?.label || (label_text ? newValue?.[label_text] : "") || "",
              value:
                newValue?.value || (value_text ? newValue?.[value_text] : "") || "",
            };
            idx !== null ? handleProductData(payload, idx) : handleProductData(payload);
          }
        }}
        getOptionSelected={(option, value) =>
          value_text === ""
            ? option?.value === value?.value
            : option?.[value_text] === value?.[value_text]
        }
        getOptionLabel={(option) =>
          typeof option === "string"
            ? option
            : option?.label || (label_text ? option?.[label_text] : "") || ""
        }
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
          if (event?.type) {
            idx !== null
              ? handleChange(newInputValue, idx, newInputValue === "")
              : handleChange(newInputValue);
          }
        }}
        options={options}
        classes={{ option: classes.option, paper: classes.paper }}
        PopperComponent={PopperComponent}
        renderInput={(params) => (
          <AutoCompleteTextField
            {...params}
            variant="outlined"
            name={name}
            data-testid={testId}
            color={
              medicine_value === null && !submitted
                ? "default"
                : submitted && inValidateValue(medicine_value, value, value_text)
                ? "error"
                : "default"
            }
            placeholder={placeholder}
          />
        )}
      />
      {submitted && inValidateValue(medicine_value, value, value_text) && (
        <div className="invalid-text">{errorMsg}</div>
      )}
    </div>
  );
};

// Controlled autocomplete
export const AutoCompleteControlled = ({
  options,
  idx,
  handleChange,
  handleProductData,
  submitted,
  placeholder = "",
  name = "",
  medicine_value = {},
  label_text = "",
  value_text = "",
  id = "",
  errorMsg = "",
  testId = "",
}) => {
  const [value, setValue] = useState(medicine_value);
  const classes = useStyles();

  return (
    <div>
      <Autocomplete
        id={id}
        data-testid={testId}
        value={medicine_value || value || null}
        onChange={(event, newValue) => {
          setValue(newValue || "");
          if (!handleProductData) {
            handleChange(newValue, id);
          } else {
            const payload = {
              innerText:
                newValue?.label || (label_text ? newValue?.[label_text] : "") || "",
              value:
                newValue?.value || (value_text ? newValue?.[value_text] : "") || "",
            };
            idx !== null ? handleProductData(payload, idx) : handleProductData(payload);
          }
        }}
        getOptionSelected={(option, value) =>
          value_text === ""
            ? option?.value === value?.value
            : option?.[value_text] === value?.[value_text]
        }
        getOptionLabel={(option) =>
          typeof option === "string"
            ? option
            : option?.label || (label_text ? option?.[label_text] : "") || ""
        }
        options={options}
        classes={{ option: classes.option, paper: classes.paper }}
        PopperComponent={PopperComponent}
        renderInput={(params) => (
          <AutoCompleteTextField
            {...params}
            variant="outlined"
            name={name}
            color={
              medicine_value === null && !submitted
                ? "default"
                : submitted && inValidateValue(medicine_value, value, value_text)
                ? "error"
                : "default"
            }
            placeholder={placeholder}
          />
        )}
      />
      {submitted && inValidateValue(medicine_value, value, value_text) && (
        <div className="invalid-text">{errorMsg}</div>
      )}
    </div>
  );
};

// Select component
export const SelectItem = ({
  value,
  options,
  idx,
  onChange,
  submitted,
  id = "",
  errorMsg = "",
  name = "",
  label_text = "",
  value_text = "",
  placeholder = "",
  testId = "",
}) => {
  const classes = useStyles();
  const safeValue =
    typeof value === "string"
      ? value
      : value?.value || (value_text ? value?.[value_text] : "") || "";

  return (
    <div>
      <Select
        id={id}
        displayEmpty
        data-testid={testId}
        value={safeValue}
        onChange={idx !== null ? (e) => onChange(e, idx) : (e) => onChange(e)}
        input={
          <SelectInput
            name={name}
            color={
              !submitted && safeValue === ""
                ? "placeholder"
                : submitted && safeValue === ""
                ? "error"
                : "default"
            }
          />
        }
        MenuProps={{
          getContentAnchorEl: null,
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          classes: { paper: classes.selectMenu },
        }}
      >
        {placeholder && (
          <StyledMenuItem disabled value="">
            {placeholder}
          </StyledMenuItem>
        )}
        {options?.length > 0 ? (
          options.map((item, index) => (
            <StyledMenuItem
              key={index}
              data-testid={id + index}
              value={
                item?.value ||
                (value_text ? item?.[value_text] : item) ||
                ""
              }
            >
              {item?.text || (label_text ? item?.[label_text] : item)}
            </StyledMenuItem>
          ))
        ) : (
          <StyledMenuItem value={null}>None...</StyledMenuItem>
        )}
      </Select>
      {submitted && safeValue === "" && (
        <div className="invalid-text">{errorMsg}</div>
      )}
    </div>
  );
};



// AutoCompleteMedicine component
export const AutoCompleteMedicine = ({
  options,
  idx,
  handleChange,
  handleProductData,
  submitted,
  placeholder = "",
  name = "",
  sub_idx,
  medicine_value = {},
  id = "",
  errorMsg = "",
  valid_error_check,
  errorMsg_Valid,
  testId = "",
  ...rest
}) => {
  const [value, setValue] = useState(medicine_value);
  const [inputValue, setInputValue] = useState("");
  const classes = useStyles();
  const isDesktop = useCustomMediaQuery("(min-width:1351px)");

  return (
    <Fragment>
      <Autocomplete
        fullWidth
        id={id}
        data-testid={testId}
        value={value || null}
        inputValue={inputValue}
        onChange={(e, newValue) => {
          setValue(newValue || "");
          if (!handleProductData) {
            handleChange(newValue);
          } else {
            const payload = {
              innerText: newValue?.label || "",
              value: newValue?.value || "",
              generic_name: newValue?.generic_name || "",
            };
            if (idx !== null && sub_idx !== null) handleProductData(payload, idx, sub_idx);
            else if (idx !== null) handleProductData(payload, idx);
            else handleProductData(payload);
          }
        }}
        filterOptions={(options, state) => {
          const state_value = state.inputValue;
          return options.filter((element) => {
            const labelMatch = element.label?.toLowerCase().includes(state_value.toLowerCase());
            const genericMatch = element.generic_name?.toLowerCase().includes(state_value.toLowerCase());
            return labelMatch || genericMatch;
          });
        }}
        getOptionSelected={(option, value) => value?.value === option?.value}
        getOptionLabel={(option) => typeof option === "string" ? option : option?.label || ""}
        onInputChange={(e, newInputValue) => {
          setInputValue(newInputValue);
          if (e?.type === "change") {
            idx !== null ? handleChange(newInputValue, idx) : handleChange(newInputValue);
          }
        }}
        options={options}
        classes={{ option: classes.option, paper: classes.paper }}
        PopperComponent={PopperComponent}
        renderInput={(params) => (
          <AutoCompleteTextField
            {...params}
            variant="outlined"
            name={name}
            data-testid={testId}
            color={submitted && inValidateValue(medicine_value, value, "") ? "error" : "default"}
            placeholder={placeholder}
            {...rest}
            style={isDesktop ? { width: 300 } : {}}
          />
        )}
      />
      {submitted && inValidateValue(medicine_value, value, "") && <div className="invalid-text">{errorMsg}</div>}
      {valid_error_check && <div className="invalid-text">{errorMsg_Valid}</div>}
    </Fragment>
  );
};

